import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import { ClassEnrichedWithUserInfo } from "@seneca/class-service-types";
import {
  SchoolEntityEnrichedWithParentInfo,
  SchoolParentConnectionsInfoResponse
} from "@seneca/schools-service-types";

import { getClient } from "services";

import {
  fetchTeacherClasses,
  fetchTeacherSchools,
  inviteSchoolClassesParents,
  inviteSchoolParents
} from "../client";
import {
  inviteSchoolClassesParentsKey,
  inviteSchoolParentsKey,
  schoolClassesKey,
  schoolParentConnectionsInfoKey,
  schoolsKey
} from "../consts";

const DAY_IN_MILLISECONDS = 60 * 60 * 24 * 1000;

export function useFetchTeacherSchools(
  opts: UseQueryOptions<SchoolEntityEnrichedWithParentInfo[]> = {}
) {
  const defaultOptions: UseQueryOptions<SchoolEntityEnrichedWithParentInfo[]> =
    {
      staleTime: DAY_IN_MILLISECONDS / 2
    };

  return useQuery<SchoolEntityEnrichedWithParentInfo[]>(
    [schoolsKey],
    fetchTeacherSchools,
    {
      ...defaultOptions,
      ...opts
    }
  );
}

export function useFetchTeacherSchoolId() {
  const { data: schools } = useFetchTeacherSchools();

  return schools?.[0]?.schoolId;
}

export function useFetchTeacherClasses(
  schoolId: string,
  opts?: UseQueryOptions<
    ClassEnrichedWithUserInfo[],
    unknown,
    ClassEnrichedWithUserInfo[],
    [typeof schoolClassesKey, string]
  >
) {
  return useQuery(
    [schoolClassesKey, schoolId],
    () => fetchTeacherClasses(schoolId),
    opts
  );
}

export function useInviteSchoolParents({
  schoolId,
  yearGroups,
  onSuccess
}: {
  schoolId: string;
  yearGroups?: number[];
  onSuccess: () => void;
}) {
  return useMutation(
    [inviteSchoolParentsKey, schoolId],
    () => inviteSchoolParents(schoolId, yearGroups),
    { onSuccess }
  );
}

export function useInviteSchoolClassesParents({
  schoolId,
  classIds,
  ...opts
}: {
  schoolId: string;
  classIds: string[];
} & UseMutationOptions) {
  return useMutation(
    [inviteSchoolClassesParentsKey, schoolId, ...classIds],
    () => inviteSchoolClassesParents(schoolId, classIds),
    opts
  );
}

export function useFetchSchoolConnectionsInfo({
  schoolId,
  startDate,
  ...opts
}: {
  schoolId: string;
  startDate: string;
} & UseQueryOptions<SchoolParentConnectionsInfoResponse>) {
  return useQuery<SchoolParentConnectionsInfoResponse>(
    [schoolParentConnectionsInfoKey, startDate],
    async () => {
      return await getClient("schoolsService").fetchSchoolConnectionInfo(
        schoolId,
        startDate
      );
    },
    { refetchOnWindowFocus: false, ...opts }
  );
}
